import { useEffect, useState } from "react";
import { closeModal, Modal, Select, TextInput } from "risa-oasis-ui_v2";
import EditableTagListCell from "../../../../../components/custom-table/tableCell/cellTypes/EditableTagListCell";
import { BiosimilarFamily, BiosimilarFamilyType } from "../types";
import { buildFamilyFromForm } from "../utils/biosimilarConfigHelpers";

/** Dialog id opened by the header Add Family button. */
export const ADD_FAMILY_MODAL_ID = "add-biosimilar-family-modal";

const FAMILY_TYPE_OPTIONS = [
  { value: "flat", label: "Flat" },
  { value: "dose_converted", label: "Dose converted" },
];

/**
 * Props for the add-family modal.
 */
interface AddFamilyModalProps {
  /** True when a family with this name already exists. */
  hasFamilyName: (name: string) => boolean;
  /** Persists the new family to Firestore. */
  onConfirm: (family: BiosimilarFamily) => Promise<void>;
}

/**
 * Modal for entering a new drug family before writing it to Firestore.
 *
 * @param props - Duplicate-name check and persist handler
 * @returns Add Family modal
 */
const AddFamilyModal = ({ hasFamilyName, onConfirm }: AddFamilyModalProps) => {
  const [family, setFamily] = useState("");
  const [type, setType] = useState<BiosimilarFamilyType>("flat");
  const [originalDrug, setOriginalDrug] = useState("");
  const [members, setMembers] = useState<string[]>([]);
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setError("");
  }, [family]);

  const reset = () => {
    setFamily("");
    setType("flat");
    setOriginalDrug("");
    setMembers([]);
    setError("");
    setSaving(false);
  };

  const handleSave = async () => {
    const trimmed = family.trim();
    if (!trimmed) {
      setError("Family name is required");
      return;
    }
    if (hasFamilyName(trimmed)) {
      setError("A family with this name already exists");
      return;
    }
    setSaving(true);
    try {
      await onConfirm(
        buildFamilyFromForm({
          family: trimmed,
          type,
          members,
          original_drug: originalDrug,
        }),
      );
      reset();
      closeModal(ADD_FAMILY_MODAL_ID);
    } catch {
      setError("Failed to save family. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      dialogId={ADD_FAMILY_MODAL_ID}
      title="Add Family"
      saveButtonText={saving ? "Saving..." : "Save"}
      cancelText="Cancel"
      autoCloseOnSave={false}
      disableSave={!family.trim() || saving}
      onSave={() => {
        void handleSave();
      }}
      onCancel={reset}
      onClose={reset}
    >
      <div className="flex flex-col gap-3 py-2">
        <TextInput
          id="add-family-name"
          label="Family name"
          placeholder="e.g. Bevacizumab"
          required
          defaultValue={family}
          error={error}
          onChange={(event) => setFamily(event.value)}
        />
        <Select
          id="add-family-type"
          label="Type"
          placeholder="Select type"
          options={FAMILY_TYPE_OPTIONS}
          defaultValue={type}
          value={type}
          onOptionChange={(option) =>
            setType((option.value as BiosimilarFamilyType) || "flat")
          }
        />
        {type === "dose_converted" ? (
          <TextInput
            id="add-family-original-drug"
            label="Original drug"
            placeholder="e.g. Aranesp"
            defaultValue={originalDrug}
            onChange={(event) => setOriginalDrug(event.value)}
          />
        ) : null}
        <div>
          <div className="mb-1 text-tiny font-semibold text-primaryGray-7">
            Members
          </div>
          <EditableTagListCell
            value={{
              values: members,
              placeholder: "Drug",
              emptyLabel: "No members",
            }}
            rowData={{ id: "add-family-members" }}
            header="Members"
            onCellValueChange={(_rowData, _fieldKey, newValue) =>
              setMembers(Array.isArray(newValue) ? (newValue as string[]) : [])
            }
          />
        </div>
      </div>
    </Modal>
  );
};

export default AddFamilyModal;
